import { prepareSessionWorktree } from './sessionEnvironment.js';
// Apolo IDE — agent store (Zustand 5).
//
// Holds workspace-level UI state: active tab, code-tab sessions list, PRs
// list, and the persisted per-user UI prefs (model, scope, branch, mode,
// permissions_mode) for the Code composer footer pills.

import { create } from 'zustand';
import { apiFetch } from '../lib/api.js';
import { isGenericName, heuristicTitle } from '../../../shared/sessionTitle.mjs';
import { hasElectronBridge } from '../lib/electronBridge.js';
import { useAuthStore } from './useAuthStore.js';
import { emptyTasksSlice, buildTasksSlice, withTaskStatus, parseSessionTasks } from './tasksModel.js';
import { buildCreateSessionPayload, insertCreatedSession } from './sessionsModel.js';
import {
  pruneWorkspacePanes, applyWorkspaceState, initialPaneState,
  focusPane as panesFocusPane,
  openSessionInWorkspace as panesOpenSession,
  closePane as panesClosePane,
  setPaneSizes as panesSetPaneSizes,
  clearPanes as panesClearPanes,
  togglePaneMaximize as panesToggleMaximize,
  setActiveSession as panesSetActiveSession,
} from './panesModel.js';

// super-marathon E3+ — the active persona this session list is scoped to (sessions are
// per-profile; everything else — GitHub, MCPs, skills, brain — stays account-level/shared).
const activeProfileId = () => useAuthStore.getState().activeProfileId || null;

function capDirectory(dir) {
  const s = typeof dir === 'string' ? dir.trim() : '';
  return s.startsWith('/') ? s : '';
}

const UI_STORAGE_KEY = 'apolo:code:ui';

// UI prefs que são intencionalmente EFÊMERAS — vivem só na memória da sessão do
// app, nunca no localStorage. Um repo do GitHub escolhido na pill é uma INTENÇÃO
// pontual ("abrir/clonar este repo na PRÓXIMA sessão"), não um default durável:
// a home injeta um "git clone <repo>" como primeira mensagem sempre que há um
// github_repo selecionado. Persistir isso fazia toda sessão nova reabrir o último
// repo pra sempre (ex.: glyco-company grudava no boot). Removemos na leitura
// (migra/limpa valores já salvos) e na escrita (nunca grava). A seleção em si
// continua funcionando em memória pra criar a sessão atual.
const EPHEMERAL_UI_KEYS = ['github_repo'];

function stripEphemeralUiPrefs(prefs) {
  if (!prefs || typeof prefs !== 'object') return prefs;
  const out = { ...prefs };
  for (const key of EPHEMERAL_UI_KEYS) delete out[key];
  return out;
}

// Default model do Code chat. Trocado de 'deepseek/deepseek-v4-pro' →
// 'google/gemini-2.5-flash' (2026-06-15): mais barato ($0.075/$0.3 vs $0.435/$0.87)
// E tool-caller mais confiável em write_file de arquivo grande (o modo de falha do
// deepseek achado na auditoria do harness). Antes disso era 'anthropic/...' mas a
// conta não tem key Anthropic. Quem quiser outro modelo troca no seletor (ou via
// OpenRouter, que proxia Claude).
export const DEFAULT_MODEL = 'google/gemini-3-flash-preview';
// Defaults LEGADOS auto-setados (não escolha explícita): migram pro atual quando
// ainda salvos no localStorage. Só migra o que era o default antigo — um modelo
// escolhido a dedo pelo user nunca casa aqui. (deepseek-v4-pro foi o default
// anterior; quem ficou nele migra pro gemini-flash.)
const LEGACY_DEFAULT_MODELS = new Set(['anthropic/claude-opus-4-7-medium', 'deepseek/deepseek-v4-pro']);
export function migrateDefaultModel(model) {
  return typeof model === 'string' && LEGACY_DEFAULT_MODELS.has(model) ? DEFAULT_MODEL : model;
}
// Workspace-panes domain (geometry helpers + state-transitions) moved to
// ./panesModel.js (E2-WS1-T02). The store imports the helpers still used by the
// session actions (applyWorkspaceState/makePaneId/pruneWorkspacePanes) + the pane
// transitions the pane actions delegate to.

export function readUiPrefs() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return {
      scope: 'local',
      branch: 'main',
      mode: 'worktree',
      permissions_mode: 'ask',
      model: DEFAULT_MODEL,
      previewPanelOpen: false,
    };
  }
  try {
    const raw = window.localStorage.getItem(UI_STORAGE_KEY);
    if (!raw) {
      return {
        scope: 'local',
        branch: 'main',
        mode: 'worktree',
        permissions_mode: 'ask',
        model: DEFAULT_MODEL,
        previewPanelOpen: false,
      };
    }
    const parsed = {
      scope: 'local',
      branch: 'main',
      mode: 'worktree',
      permissions_mode: 'ask',
      model: DEFAULT_MODEL,
      previewPanelOpen: false,
      ...JSON.parse(raw),
    };
    // Migra o default legado salvo (anthropic sem key) → DeepSeek. Só toca o que
    // era o default antigo; escolha explícita do user fica intacta.
    parsed.model = migrateDefaultModel(parsed.model);
    // Limpa prefs efêmeras que ficaram salvas em localStorage de versões antigas
    // (ex.: github_repo grudado em glyco-company), pra a home não reabrir o repo.
    return stripEphemeralUiPrefs(parsed);
  } catch {
    return {
      scope: 'local',
      branch: 'main',
      mode: 'worktree',
      permissions_mode: 'ask',
      model: DEFAULT_MODEL,
      previewPanelOpen: false,
    };
  }
}

function writeUiPrefs(prefs) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(UI_STORAGE_KEY, JSON.stringify(stripEphemeralUiPrefs(prefs)));
  } catch {
    // ignore quota / private-mode errors
  }
}

// BUG 2026-07-05 — o cap da largura do preview era 92% da janela; ao arrastar largo
// o preview (aside shrink-0) engolia a área e o chat colapsava pra 0 ("chat sumiu").
// Agora deixa SEMPRE ≥520px pra sidebar + chat. Escala com a janela (maior em
// monitores grandes) mas nunca mata o chat. Aplicado na ESCRITA (setPreviewWidth) e
// na LEITURA (useDevPreview) — a leitura destrava um valor gigante já salvo.
export function clampPreviewWidth(width) {
  const winW = typeof window !== 'undefined' && window.innerWidth ? window.innerWidth : 1600;
  const cap = Math.max(400, winW - 520);
  return Math.max(300, Math.min(cap, Math.round(Number(width)) || 360));
}

export const useAgentStore = create((set, get) => {
  const initialPaneBoot = initialPaneState();

  return {
  activeWorkspace: 'code', // 'code' | 'chat' | 'plan' | 'council' | 'god' | 'dispatch'
  code: {
    sessions: [],
    folders: [],
    expandedFolders: {},
    activeSessionId: initialPaneBoot.activeSessionId,
    workspacePanes: initialPaneBoot.workspacePanes,
    focusedPaneId: initialPaneBoot.focusedPaneId,
    // (Pedro) which pane is maximized to full width (others hidden). Transient
    // UI state — not persisted. null = normal split.
    maximizedPaneId: null,
    // v10.7: mensagem inicial pendente quando uma sessão é criada a partir
    // do composer da home ("Criar sessão"). SessionWorkspace auto-envia no
    // mount e limpa. #16 — mapa POR sessionId: { [sessionId]: { text, attachments } }.
    pendingFirstMessage: {},
    // #12 — modo do Chat (ask/debug/plan/…) PERSISTE por sessão (antes era só um
    // prefixo one-shot na 1ª msg que evaporava do turno 2). SessionWorkspace lê pra
    // reaplicar o gate readOnly em TODO turno. { [sessionId]: 'ask'|'debug'|... }.
    chatModes: {},
    // P3.2 — abas de SESSÃO persistentes (estilo navegador): toda sessão aberta vira uma
    // pill que PERSISTE tanto na aba Code quanto na Chat; clicar alterna a sessão E troca
    // a surface (code/chat) automaticamente, sem o user clicar no header. { id, kind, title }[].
    openTabs: [],
    prs: [],
    prsSource: null,
    prsError: null,
    // Status derivado por sessão (mapa { [sessionId]: SessionStatus }), vindo de
    // GET /api/code/sessions/statuses. Derivado do event log, não da coluna status.
    sessionStatuses: {},
    // Glow ao vivo (client-only): ids das sessões streamando AGORA no renderer.
    // useStreamingChat liga/desliga. A sidebar pinta essas como 'running' (verde)
    // na hora — sem depender do poll do session_events (que decai pra idle após
    // 48h e nunca grava turn_end). Mapa usado como Set: { [sessionId]: true }.
    streamingSessionIds: {},
    // Auditoria 2026-07-02 — metadados das runs vivas ({ [sessionId]: { name,
    // profileId } }), capturados quando o streaming começa. Sobrevivem à sessão
    // sair da lista na troca de perfil, pra a barra de runs em segundo plano
    // mostrar nome + oferecer "Abrir" (voltar pro perfil da run).
    runMeta: {},
    loading: false,
    error: null,
    ui: readUiPrefs(),
    sidebarSearch: '',
    // Sprint tasking-cursor-ide P3-T05 — Tasking layer slice (Kiro DAG).
    // Shape per 03-PLAN §6: { runId, tasks, statusByPlanId, criticalPath }.
    // Persisted to code_sessions.flags_json.tasks (no migration). Cleared on
    // session switch unless the new session has a persisted snapshot.
    tasks: {
      runId: null,
      tasks: [],
      statusByPlanId: {},
      criticalPath: [],
      // (T6) optional Mission Control plan-doc header { title, summary } shown
      // above the DAG in the Tasks rail. Additive to the §6 contract; rides
      // flags_json with the rest of code.tasks. null = no header.
      planDoc: null,
      // (T3) ISO timestamp of the human DAG approval (rail "Aprovar DAG"). null =
      // awaiting approval. Reset to null on every new plan (setCodeTasks).
      approvedAt: null,
    },
    // Sprint honest-green-merge-queue P4 — Merge Queue read-model (the
    // buildQueueView DTO from apolo-api/src/lib/mergeQueue.js). The queue runs
    // in the worker/Mac loop; this slice holds its VERDICT-ONLY view for the
    // Glass Box lane. null = no queue. Persisted in flags_json.mergeQueue (no
    // migration). Nothing here lands a PR — landing is human-gated.
    mergeQueue: null,
    // Sprint hgmq-finish P7 — durable spec (Track C). null until a plan is
    // approved; ephemeral in the Glass Box during the turn, persisted to
    // .apolo/specs/<slug>/{requirements,design,tasks}.md ON approval.
    specs: null,
  },

  // Plan tab — workspace de criação de sprints (sprint plan-tab-v1).
  // E0 slice; E2 acrescenta actions (setActivePlan, setPlans, set UI prefs).
  plan: {
    activePlanId: null,
    plans: [],
    loading: false,
    error: null,
    ui: { sidebar: 'tree', chatOpen: true, sidebarCollapsed: false },
  },

  setActivePlan(id) {
    set((state) => ({ plan: { ...state.plan, activePlanId: id } }));
  },

  setPlans(plans) {
    set((state) => ({ plan: { ...state.plan, plans: Array.isArray(plans) ? plans : [] } }));
  },

  setPlanLoading(loading) {
    set((state) => ({ plan: { ...state.plan, loading: !!loading } }));
  },

  setPlanError(error) {
    set((state) => ({ plan: { ...state.plan, error: error || null } }));
  },

  setPlanUiPref(key, value) {
    set((state) => ({
      plan: { ...state.plan, ui: { ...state.plan.ui, [key]: value } },
    }));
  },

  setActiveWorkspace(ws) {
    // Hermes era uma superfície visual; o runtime continua global. Estados
    // antigos devem cair em Code, nunca reabrir uma tela removida.
    set({ activeWorkspace: ws === 'hermes' ? 'code' : ws });
  },

  setActiveSession(id) {
    const prevId = get().code.activeSessionId;
    // Pane state-transition (5 branches + plan_mode reset) → panesModel.setActiveSession.
    set((state) => ({ code: { ...state.code, ...panesSetActiveSession(state.code, id) } }));
    // Cross-domain: reidrata/limpa o slice de tasks pra a sessão-alvo, SÓ quando MUDA
    // (evita clobber do DAG vivo numa re-ativação da mesma sessão, ainda não persistido).
    if (id !== prevId) {
      if (id == null) get().resetCodeTasks();
      else get().loadCodeTasksFromSession(get().code.sessions.find((s) => s.id === id) || null);
    }
  },

  focusPane(paneId) {
    set((state) => {
      const patch = panesFocusPane(state.code, paneId);
      return patch ? { code: { ...state.code, ...patch } } : state;
    });
  },

  togglePaneMaximize(paneId) {
    set((state) => ({ code: { ...state.code, ...panesToggleMaximize(state.code, paneId) } }));
  },

  openSessionInWorkspace(sessionId, { mode = 'split' } = {}) {
    const { patch, result } = panesOpenSession(get().code, sessionId, mode);
    if (patch) set((s) => ({ code: { ...s.code, ...patch } }));
    return result;
  },

  closePane(paneId) {
    set((state) => ({ code: { ...state.code, ...panesClosePane(state.code, paneId) } }));
  },

  setPaneSizes(fractions) {
    set((state) => {
      const patch = panesSetPaneSizes(state.code, fractions);
      return patch ? { code: { ...state.code, ...patch } } : state;
    });
  },

  clearWorkspacePanes() {
    set((state) => ({ code: { ...state.code, ...panesClearPanes() } }));
  },

  // v10.7: arma/desarma a primeira mensagem que o SessionWorkspace deve
  // auto-enviar ao montar (vinda do composer "Criar sessão").
  // Auditoria 2026-07-02 (chat-hardening S2 #2): aceita `attachments` opcional —
  // antes a home do Chat anexava arquivos que eram DESCARTADOS no submit (a
  // affordance parecia funcionar e o agente nunca recebia o arquivo). in-memory
  // (pendingFirstMessage não persiste em localStorage), sem inchar.
  // Auditoria 2026-07-03 (#16): mapa POR sessionId, não slot único global — criar
  // sessões em sequência rápida (ex.: abas novas) sobrescrevia o slot e a mensagem de
  // abertura da sessão anterior se perdia. Cada sessão guarda a sua; o auto-fire consome
  // a entrada do seu próprio id e limpa só a dela.
  setPendingFirstMessage(sessionId, text, attachments = null, durable = false) {
    set((state) => ({
      code: {
        ...state.code,
        pendingFirstMessage: {
          ...(state.code.pendingFirstMessage || {}),
          [sessionId]: { text, attachments: attachments || null, ...(durable ? { durable: true } : {}) },
        },
      },
    }));
  },
  clearPendingFirstMessage(sessionId) {
    set((state) => {
      const map = { ...(state.code.pendingFirstMessage || {}) };
      if (sessionId == null) return { code: { ...state.code, pendingFirstMessage: {} } };
      delete map[sessionId];
      return { code: { ...state.code, pendingFirstMessage: map } };
    });
  },
  // #11/#12 — persiste o modo do Chat por sessão (ask/debug/plan/…). 'ask' vira gate
  // readOnly DE VERDADE (não só prosa) no loop, reaplicado em todo turno.
  setChatMode(sessionId, mode) {
    if (!sessionId) return;
    const current = get().code.sessions.find((s) => s.id === sessionId);
    const readOnly = current?.flags?.read_only || ['ask', 'research'].includes(current?.flags?.collaboration_mode) || ['ask', 'research'].includes(get().code.chatModes[sessionId]) || ['ask', 'research'].includes(mode);
    const flags = { collaboration_mode: mode || null, ...(readOnly ? { read_only: true } : {}) };
    void get().updateCodeSession(sessionId, { flags });
    set((state) => ({
      code: { ...state.code, sessions: state.code.sessions.map((s) => s.id === sessionId ? { ...s, flags: { ...s.flags, ...flags } } : s), chatModes: { ...(state.code.chatModes || {}), [sessionId]: mode || null } },
    }));
  },
  // P3.2 — upsert de uma aba de sessão (dedupe por id; atualiza title/kind se já existe).
  openSessionTab(tab) {
    if (!tab || !tab.id) return;
    set((state) => {
      const tabs = state.code.openTabs || [];
      const idx = tabs.findIndex((t) => t.id === tab.id);
      const entry = { id: tab.id, kind: tab.kind === 'chat' ? 'chat' : 'code', title: tab.title || '(sessão)' };
      const openTabs = idx === -1 ? [...tabs, entry] : tabs.map((t, i) => (i === idx ? entry : t));
      return { code: { ...state.code, openTabs } };
    });
  },
  // P3.2 — fecha uma aba; se era a ativa, ativa a vizinha (e troca a surface pro kind dela).
  closeSessionTab(id) {
    const state = get();
    const tabs = state.code.openTabs || [];
    const idx = tabs.findIndex((t) => t.id === id);
    if (idx === -1) return;
    const openTabs = tabs.filter((t) => t.id !== id);
    set((s) => ({ code: { ...s.code, openTabs } }));
    if (state.code.activeSessionId === id) {
      const neighbor = openTabs[idx] || openTabs[idx - 1] || null;
      if (neighbor) {
        get().setActiveWorkspace(neighbor.kind === 'chat' ? 'chat' : 'code');
        get().setActiveSession(neighbor.id);
      } else {
        get().setActiveSession(null);
      }
    }
  },

  setSidebarSearch(query) {
    set((state) => ({ code: { ...state.code, sidebarSearch: query } }));
  },

  setUiPref(key, value) {
    set((state) => {
      const ui = { ...state.code.ui, [key]: value };
      writeUiPrefs(ui);
      return { code: { ...state.code, ui } };
    });
  },

  togglePreviewPanel() {
    const open = Boolean(get().code.ui.previewPanelOpen);
    get().setUiPref('previewPanelOpen', !open);
  },

  setPreviewPanelOpen(open) {
    get().setUiPref('previewPanelOpen', Boolean(open));
  },

  // sprint code-preview-engine E2-WS3 — preview rail sub-state (persisted via
  // setUiPref like the other code.ui prefs). Consumers default when undefined
  // (previewTab ?? 'preview', previewDevice ?? 'desktop') so readUiPrefs stays
  // untouched.
  setPreviewTab(tab) {
    get().setUiPref('previewTab', tab);
  },

  setPreviewDevice(device) {
    get().setUiPref('previewDevice', device);
  },

  // v3 E2 — resizable preview rail width (drag handle). clampPreviewWidth keeps the
  // chat usable (see its comment); consumers default to 360 when undefined.
  setPreviewWidth(width) {
    get().setUiPref('previewWidth', clampPreviewWidth(width));
  },

  toggleDesignMode() {
    const on = Boolean(get().code.ui.designModeOn);
    get().setUiPref('designModeOn', !on);
    // Selecionar e Anotar são modos EXCLUSIVOS no guest (um picker por vez).
    if (!on) get().setUiPref('annotateModeOn', false);
  },

  // Modo Anotar (to-dos numerados no preview, paridade Cursor browser): clicar
  // elementos abre popup de comentário → pills numeradas no composer.
  toggleAnnotateMode() {
    const on = Boolean(get().code.ui.annotateModeOn);
    get().setUiPref('annotateModeOn', !on);
    if (!on) get().setUiPref('designModeOn', false);
  },

  async refreshSessions() {
    set((state) => ({ code: { ...state.code, loading: true, error: null } }));
    const pid = activeProfileId();
    const res = await apiFetch(`/api/code/sessions${pid ? `?profile_id=${encodeURIComponent(pid)}` : ''}`);
    if (!res.ok) {
      set((state) => ({ code: { ...state.code, loading: false, error: res.error || 'falha' } }));
      return;
    }
    set((state) => ({
      code: {
        ...state.code,
        loading: false,
        sessions: res.data.sessions ?? [],
        chatModes: Object.fromEntries((res.data.sessions ?? []).map((session) => [session.id, session.flags?.collaboration_mode || state.code.chatModes[session.id] || null])),
        ...(() => {
          const pruned = pruneWorkspacePanes(
            state.code.workspacePanes || [],
            (res.data.sessions ?? []).map((s) => s.id),
          );
          if (pruned.length === 0) {
            return { workspacePanes: [], focusedPaneId: null, activeSessionId: null };
          }
          const focused =
            state.code.focusedPaneId && pruned.some((p) => p.id === state.code.focusedPaneId)
              ? state.code.focusedPaneId
              : pruned[0]?.id ?? null;
          return applyWorkspaceState(pruned, focused);
        })(),
      },
    }));
  },

  async refreshFolders() {
    const res = await apiFetch('/api/code/folders');
    if (!res.ok) return;
    set((state) => ({ code: { ...state.code, folders: res.data.folders ?? [] } }));
  },

  async createFolder(name) {
    const res = await apiFetch('/api/code/folders', { method: 'POST', body: { name } });
    if (!res.ok) return { ok: false, error: res.error };
    set((state) => ({
      code: { ...state.code, folders: [...state.code.folders, res.data.folder] },
    }));
    return { ok: true, folder: res.data.folder };
  },

  async renameFolder(id, name) {
    const res = await apiFetch(`/api/code/folders/${id}`, { method: 'PATCH', body: { name } });
    if (!res.ok) return { ok: false, error: res.error };
    set((state) => ({
      code: {
        ...state.code,
        folders: state.code.folders.map((f) => (f.id === id ? { ...f, name } : f)),
      },
    }));
    return { ok: true };
  },

  // Folder color (backend column already exists; PATCH {color}). '' clears it.
  async setFolderColor(id, color) {
    const res = await apiFetch(`/api/code/folders/${id}`, { method: 'PATCH', body: { color: color || '' } });
    if (!res.ok) return { ok: false, error: res.error };
    set((state) => ({
      code: {
        ...state.code,
        folders: state.code.folders.map((f) => (f.id === id ? { ...f, color: color || '' } : f)),
      },
    }));
    return { ok: true };
  },

  async deleteFolder(id) {
    const res = await apiFetch(`/api/code/folders/${id}`, { method: 'DELETE' });
    if (!res.ok) return { ok: false, error: res.error };
    set((state) => ({
      code: {
        ...state.code,
        folders: state.code.folders.filter((f) => f.id !== id),
        sessions: state.code.sessions.map((s) =>
          s.folder_id === id ? { ...s, folder_id: null } : s,
        ),
      },
    }));
    return { ok: true };
  },

  async moveSessionToFolder(sessionId, folderId) {
    const res = await apiFetch('/api/code/folders/move', {
      method: 'POST',
      body: { session_id: sessionId, folder_id: folderId },
    });
    if (!res.ok) return { ok: false, error: res.error };
    set((state) => ({
      code: {
        ...state.code,
        sessions: state.code.sessions.map((s) =>
          s.id === sessionId ? { ...s, folder_id: folderId || null } : s,
        ),
      },
    }));
    return { ok: true };
  },

  toggleFolderExpanded(id) {
    set((state) => ({
      code: {
        ...state.code,
        expandedFolders: {
          ...state.code.expandedFolders,
          [id]: !state.code.expandedFolders[id],
        },
      },
    }));
  },

  async refreshPRs() {
    const res = await apiFetch('/api/code/prs');
    if (!res.ok) return;
    set((state) => ({
      code: {
        ...state.code,
        prs: res.data.prs ?? [],
        prsSource: res.data.source,
        prsError: res.data.github_error ?? null,
      },
    }));
  },

  // Status derivado por sessão. Polled pela aba Code (ver useSessionStatusPoll).
  // Degrada graciosamente: falha → mantém o mapa atual (sem piscar pra vazio).
  async refreshSessionStatuses() {
    const res = await apiFetch('/api/code/sessions/statuses');
    if (!res.ok) return { ok: false, error: res.error };
    set((state) => ({
      code: { ...state.code, sessionStatuses: res.data.statuses ?? {} },
    }));
    return { ok: true };
  },

  // Glow ao vivo: marca/desmarca uma sessão como "streamando agora". Otimista,
  // client-only — o dot vira verde (running) na hora, sem esperar o poll do
  // backend (que pode estar atrasado, fora da janela de 48h, ou sem turn_end).
  // Chamado por useStreamingChat no início/fim de cada stream.
  setSessionStreaming(sessionId, isStreaming) {
    if (!sessionId) return;
    set((state) => {
      const current = state.code.streamingSessionIds || {};
      if (Boolean(current[sessionId]) === Boolean(isStreaming)) return state; // no-op
      const next = { ...current };
      const nextMeta = { ...(state.code.runMeta || {}) };
      if (isStreaming) {
        next[sessionId] = true;
        // Captura nome + perfil AGORA (a sessão ainda está visível no perfil atual).
        const sess = (state.code.sessions || []).find((s) => s.id === sessionId);
        nextMeta[sessionId] = {
          name: sess?.name || '(sessão)',
          profileId: sess?.profile_id ?? activeProfileId(),
        };
      } else {
        delete next[sessionId];
        delete nextMeta[sessionId];
      }
      return { code: { ...state.code, streamingSessionIds: next, runMeta: nextMeta } };
    });
  },

  async createCodeSession(input) {
    // BIRTH CONTRACT → sessionsModel: ui-default inheritance + strict profile scoping.
    const payload = buildCreateSessionPayload(input, get().code.ui, activeProfileId());
    if (input.client_intent_id) payload.flags = { ...payload.flags, requested_directory: input.directory || null, requested_repo: input.github_repo?.full_name || null };
    const prepareEnvironment = !!input.client_intent_id && (!!input.github_repo || payload.mode === 'worktree');
    if (prepareEnvironment) payload.flags = { ...payload.flags, environment_state: 'preparing' };
    const res = await apiFetch('/api/code/sessions', { method: 'POST', body: payload });
    if (!res.ok) return { ok: false, error: res.error, status: res.status };

    let session = res.data.session;

    // Electron: detect ~/Apolo (or saved repo) so tools always have an absolute root.
    let sourceRepo = capDirectory(session.directory);
    let resolvedBranch = null;

    // composer-anchor D5: quando a sessão nasce de um repo do GitHub escolhido na
    // pill, resolve pra um checkout LOCAL determinístico (reusa o existente —
    // ex. ~/glyco-company — ou clona em ~/.apolo/repos via IPC) em vez de pedir
    // pro LLM rodar `git clone`. Esse caminho vira o sourceRepo do worktree (ou o
    // diretório direto). Precede o repo.detect (que só conhece o repo do Apolo).
    if (input.github_repo?.full_name && hasElectronBridge() && window.apolo?.repo?.resolveRemote) {
      try {
        const r = await window.apolo.repo.resolveRemote({
          fullName: input.github_repo.full_name,
          cloneUrl: input.github_repo.clone_url || input.github_repo.url,
          defaultBranch: input.github_repo.default_branch || payload.branch || 'main',
        });
        if (r?.ok && typeof r.path === 'string' && r.path.startsWith('/')) {
          sourceRepo = r.path;
          resolvedBranch = r.branch || null;
        } else {
          return { ok: false, session, error: r?.error || 'Não foi possível preparar o repositório escolhido.' };
        }
      } catch (err) {
        return { ok: false, session, error: err?.message || 'Falha ao preparar repositório.' };
      }
    }

    // An explicitly CHOSEN local folder (NewSessionModal "Pasta" picker) wins
    // over the ~/Apolo auto-detect below — that's the whole point of picking a
    // folder. Must precede repo.detect (which only ever finds the Apolo repo).
    if (!sourceRepo && typeof input.directory === 'string' && input.directory.startsWith('/')) {
      sourceRepo = capDirectory(input.directory);
    }

    // guard PR #618 — sessão de CHAT sem alvo EXPLÍCITO (pasta/repo escolhidos no
    // composer) NÃO herda o repo principal auto-detectado (~/Apolo): foi assim que
    // um chat da home, em modo direct, commitou e abriu PR no checkout do usuário.
    // Chat sem alvo = sessão SEM root (conversacional); quem quer código escolhe a
    // pasta no picker. Sessões de CODE mantêm o auto-detect de sempre.
    const wantsAutoRepo = !input.client_intent_id && payload.kind !== 'chat';
    if (hasElectronBridge() && window.apolo?.repo?.detect && !sourceRepo && wantsAutoRepo) {
      try {
        const det = await window.apolo.repo.detect();
        if (det?.ok && det.path?.startsWith('/')) sourceRepo = det.path;
      } catch (err) {
        console.warn('[apolo] repo detect failed:', err?.message || err);
      }
    }

    if (sourceRepo && !session.directory) {
      const persistDir = await apiFetch(`/api/code/sessions/${session.id}`, {
        method: 'PUT',
        body: { directory: sourceRepo },
      });
      if (!persistDir.ok) return { ok: false, session, error: persistDir.error || 'Não foi possível salvar o contexto. Rascunho preservado.' };
      session = { ...session, directory: sourceRepo };
    }

    // Epic 9: if the session asked for worktree isolation and we are inside
    // the Electron renderer, create the real git worktree now and persist
    // its path/branch on the server. In the browser/jsdom (no bridge),
    // the session stays "logical" — UI keeps working, worktree_path empty.
    if (prepareEnvironment && session.mode === 'worktree' && !hasElectronBridge()) return { ok: false, session, error: 'Abra no aplicativo desktop para preparar o worktree.' };
    if (input.client_intent_id && input.github_repo && !window.apolo?.repo?.resolveRemote) return { ok: false, session, error: 'Resolver de repositórios indisponível neste ambiente.' };
    if (session.mode === 'worktree' && hasElectronBridge() && !session.worktree_path) {
      if (!sourceRepo && input.client_intent_id) return { ok: false, session, error: 'Escolha um projeto antes de criar um worktree.' };
      const prepared = await prepareSessionWorktree(session, { sourceRepo, base: resolvedBranch, bridge: window.apolo, apiFetch });
      if (!prepared.ok) return prepared;
      session = prepared.session;
    }

    if (prepareEnvironment) {
      const ready = await apiFetch(`/api/code/sessions/${session.id}`, { method: 'PUT', body: { flags: { environment_state: 'ready' } } });
      if (!ready.ok) return { ok: false, session, error: ready.error || 'Não foi possível confirmar o ambiente preparado.' };
      session = ready.data.session;
    }

    if ((payload.profile_id || null) !== (activeProfileId() || null)) return { ok: true, session, inactiveProfile: true };

    // ACTIVATION → sessionsModel: prepend + workspace takeover + plan_mode reset.
    // createCodeSession ativa a sessão AQUI (não via setActiveSession), então o reset
    // do chip Plan precisa acontecer neste caminho também.
    set((state) => ({ code: { ...state.code, ...insertCreatedSession(state.code, session) } }));
    // #11-paralelo — createCodeSession ativa a sessão inline (não via
    // setActiveSession), então limpa o slice de tasks AQUI também: sessão NOVA
    // começa com task graph vazio, não herda o DAG da anterior.
    get().resetCodeTasks();
    return { ok: true, session };
  },

  async updateCodeSession(id, patch) {
    const res = await apiFetch(`/api/code/sessions/${id}`, { method: 'PUT', body: patch });
    if (!res.ok) return { ok: false, error: res.error };
    set((state) => ({
      code: {
        ...state.code,
        sessions: state.code.sessions.map((s) => (s.id === id ? res.data.session : s)),
      },
    }));
    return { ok: true, session: res.data.session };
  },

  // Auto-título (recents-claude-style): quando a 1ª mensagem de uma sessão de nome
  // GENÉRICO ("New session"/"General coding session") chega, dá um nome heurístico
  // na hora (primeiras palavras úteis da mensagem). Best-effort: NUNCA quebra o
  // 1º turno (o dispatch chama fire-and-forget, sem await).
  async autoTitleFromFirstMessage(sessionId, text) {
    try {
      const s = get().code.sessions.find((x) => x.id === sessionId);
      if (!s || !isGenericName(s.name)) return;
      const title = heuristicTitle(text);
      if (title) await get().updateCodeSession(sessionId, { name: title });
      // Refino por LLM (3-5 palavras) em background — passa o texto pra evitar
      // corrida com a persistência da 1ª msg. Best-effort; atualiza o name se voltar.
      apiFetch(`/api/code/sessions/${sessionId}/retitle`, { method: 'POST', body: { text } })
        .then((r) => {
          if (!(r?.ok && r.data?.refined && r.data?.session)) return;
          set((state) => ({ code: { ...state.code, sessions: state.code.sessions.map((x) => (x.id === sessionId ? r.data.session : x)) } }));
        })
        .catch(() => {});
    } catch { /* best-effort: auto-título nunca atrapalha o turno */ }
  },

  // Backfill: renomeia todas as sessões genéricas existentes a partir da 1ª
  // mensagem de cada uma (server-side). Retorna quantas foram renomeadas e
  // recarrega a lista pra a sidebar refletir.
  async backfillSessionTitles() {
    const res = await apiFetch('/api/code/sessions/backfill-titles', { method: 'POST' });
    if (res.ok) await get().refreshSessions?.();
    return res.ok ? { ok: true, renamed: res.data?.renamed || 0 } : { ok: false, error: res.error };
  },

  // ── Sprint tasking-cursor-ide P3-T05 — Tasking layer actions ───────────
  // The shape (`code.tasks = { runId, tasks, statusByPlanId, criticalPath }`)
  // is the frozen 03-PLAN §6 contract. Persistence rides `flags_json` (no
  // migration). The DAG model itself is in apolo/src/lib/tasks/taskGraph.js
  // — these actions are thin wrappers that keep the lib pure and the store
  // immutable.

  setCodeTasks(slice) {
    // Local-only state replacement (shape/defaults in tasksModel). Caller is
    // responsible for persisting via persistCodeTasks(sessionId) when appropriate.
    set((state) => ({ code: { ...state.code, tasks: buildTasksSlice(slice) } }));
  },

  resetCodeTasks() {
    set((state) => ({ code: { ...state.code, tasks: emptyTasksSlice() } }));
  },

  // Sprint honest-green-merge-queue P4 — set the Merge Queue read-model view
  // (the buildQueueView DTO). VERDICT-ONLY: this never lands a PR. Pass null
  // to clear the lane.
  setMergeQueueView(view) {
    set((state) => ({
      code: {
        ...state.code,
        mergeQueue: view && typeof view === 'object' ? view : null,
      },
    }));
  },

  // P4-T02 — pull the merge-queue read-model from the worker into the store.
  // Best-effort: leaves the lane unchanged on error (never throws to the hook).
  async refreshMergeQueue(sessionId) {
    if (!sessionId) return;
    try {
      const res = await apiFetch(`/api/code-ide/sessions/${sessionId}/merge-queue`);
      if (res?.ok && res.data?.view) get().setMergeQueueView(res.data.view);
    } catch {
      // best-effort poll — never break the panel render on a network hiccup
    }
  },

  // P4-T02 — post a queue mutation (action: 'verdict'|'stale'|'landed'). The
  // renderer Mac loop runs the prospective-merge rebase + REUSES the local
  // oracle, then posts the result here. Returns the updated view (and stores it).
  async postMergeQueueAction(sessionId, body) {
    if (!sessionId) return { ok: false, error: 'no sessionId' };
    const res = await apiFetch(`/api/code-ide/sessions/${sessionId}/merge-queue`, { method: 'POST', body });
    if (res.ok && res.data?.view) get().setMergeQueueView(res.data.view);
    return res;
  },

  // Sprint hgmq-finish P7 — durable spec (Track C). setSpec stores the
  // (ephemeral) spec in the Glass Box; persistSpec writes it to
  // .apolo/specs/<slug>/ ON human approval (the write boundary) and stamps
  // approvedAt so it reads as durable.
  setSpec(spec) {
    set((state) => ({ code: { ...state.code, specs: spec && typeof spec === 'object' ? spec : null } }));
  },

  async persistSpec(sessionId, spec) {
    const session = get().code.sessions.find((s) => s.id === sessionId);
    const root = session?.directory || session?.worktree_path || '';
    // dynamic import keeps the pure model out of the store's static graph.
    const { specPaths, serializeSpec } = await import('../lib/specs/specModel.js');
    const paths = specPaths(spec?.feature);
    const files = serializeSpec(spec);
    const ioOk = typeof window !== 'undefined' && window.apolo?.file?.write && root;
    let written = false;
    if (ioOk) {
      try {
        await window.apolo.file.write({ root, filePath: paths.requirements, content: files.requirements });
        await window.apolo.file.write({ root, filePath: paths.design, content: files.design });
        await window.apolo.file.write({ root, filePath: paths.tasks, content: files.tasks });
        written = true;
      } catch {
        written = false; // disk write best-effort; the spec still lives in the Glass Box
      }
    }
    const durable = { ...spec, approvedAt: Date.now(), paths, persisted: written };
    get().setSpec(durable);
    // Feed the derived DAG into code.tasks (durable tasks.md → the task layer).
    if (Array.isArray(spec?.tasks?.derived) && spec.tasks.derived.length) {
      get().setCodeTasks?.({ runId: `spec:${paths.slug}`, tasks: spec.tasks.derived, statusByPlanId: {}, criticalPath: [] });
    }
    return { ok: true, written, paths };
  },

  // (T3) Human-approve the projected DAG from the persistent Tasks rail (the
  // "Aprovar DAG" gate — Mission Control style). Stamps approvedAt so the rail
  // flips to an approved state; persists best-effort. The actual execution turn
  // is kicked by the SessionWorkspace listener (apolo:approveDag), keeping this
  // store action pure-state. New plans reset approvedAt via setCodeTasks.
  approveCodeTasks(sessionId) {
    set((state) => ({
      code: {
        ...state.code,
        tasks: { ...state.code.tasks, approvedAt: new Date().toISOString() },
      },
    }));
    if (sessionId) {
      try { get().persistCodeTasks?.(sessionId); } catch { /* best-effort */ }
    }
  },

  // Update a single task's status (immutable — tasksModel.withTaskStatus).
  markCodeTaskStatus(taskId, status) {
    set((state) => ({ code: { ...state.code, tasks: withTaskStatus(state.code.tasks, taskId, status) } }));
  },

  // Pull the per-session snapshot from session.flags_json.tasks (set when
  // the session was last persisted). Falls back to a clean slice. Safe to
  // call with any session object — defends against malformed JSON.
  loadCodeTasksFromSession(session) {
    get().setCodeTasks(parseSessionTasks(session) || emptyTasksSlice());
  },

  // Write the current task slice back to D1 via updateCodeSession,
  // preserving any other flags_json keys (e.g. ui prefs the session row
  // may already carry). Best-effort: returns the result envelope.
  async persistCodeTasks(sessionId) {
    if (!sessionId) return { ok: false, error: 'no sessionId' };
    const state = get();
    const session = state.code.sessions.find((s) => s.id === sessionId);
    let fj = null;
    if (session?.flags_json) {
      if (typeof session.flags_json === 'string') {
        try { fj = JSON.parse(session.flags_json); } catch { fj = {}; }
      } else if (typeof session.flags_json === 'object') {
        fj = { ...session.flags_json };
      }
    }
    const next = { ...(fj || {}), tasks: state.code.tasks };
    // FASE-11 E6-WS1-T01 — a rota PUT /code/sessions/:id lê `body.flags` (mergeFlags→flags_json),
    // NÃO `body.flags_json`. Enviar `flags_json` fazia a rota IGNORAR o patch → o DAG de tasks
    // NUNCA persistia (sumia no reload). Envia `flags` pra a rota casar e gravar em flags_json.
    return state.updateCodeSession(sessionId, { flags: next });
  },

  // Sprint v2 Epic B5 WS B5.3 — explicit /save command + manual save.
  async saveSessionToBrain(sessionId) {
    const res = await apiFetch(`/api/code/sessions/${sessionId}/save-to-brain`, { method: 'POST' });
    if (!res.ok) return { ok: false, error: res.error };
    return { ok: true, ...res.data };
  },

  async listSubagents(sessionId) {
    const res = await apiFetch(`/api/code-ide/sessions/${sessionId}/subagent`);
    if (!res.ok) return { ok: false, error: res.error };
    return { ok: true, subagents: res.data.subagents ?? [] };
  },

  async dispatchSubagent(sessionId, { task, role }) {
    const res = await apiFetch(`/api/code-ide/sessions/${sessionId}/subagent`, {
      method: 'POST',
      body: { task, role },
    });
    if (!res.ok) return { ok: false, error: res.error };
    return { ok: true, subagent: res.data.subagent };
  },

  // Plan Mode v2 (E5): server-side, SESSION-LEVEL persistence only — it writes
  // `mode_state` on the session row and is the path the side-panel PlanTab uses.
  // It does NOT (and must not) touch `code.ui.plan_mode`, which is the LOCAL chip
  // pref that solely drives the live planPropose flow in handleSend. Kept separate
  // so a PlanTab toggle can't silently kick off a propose turn. See PlanTab.jsx.
  async togglePlanMode(sessionId, planMode) {
    const res = await apiFetch(`/api/code-ide/sessions/${sessionId}/plan`, {
      method: 'PUT',
      body: { planMode: !!planMode },
    });
    if (!res.ok) return { ok: false, error: res.error };
    set((state) => ({
      code: {
        ...state.code,
        sessions: state.code.sessions.map((s) =>
          s.id === sessionId ? { ...s, mode_state: res.data.mode_state } : s,
        ),
      },
    }));
    return { ok: true, planMode: res.data.planMode, mode_state: res.data.mode_state };
  },

  async deleteCodeSession(id) {
    // Tear the worktree FS down first (if any) so we don't leave orphan
    // directories under ~/.apolo/worktrees/. Worker bookkeeping clears
    // worktree_path before the session row is soft-deleted.
    const session = get().code.sessions.find((s) => s.id === id);
    if (session?.worktree_path && hasElectronBridge()) {
      let rm;
      try {
        rm = await window.apolo.worktree.remove({ sessionId: id });
      } catch (err) {
        console.warn('[apolo] worktree removal failed:', err?.message || err);
      }
      // Data-loss guard (bug-review Wave 1): removeWorktree now REFUSES to
      // --force over uncommitted work. If it's dirty, confirm before discarding
      // and only then retry with force:true. Declining aborts the whole delete.
      if (rm && rm.removed === false && rm.reason === 'dirty') {
        const n = rm.changes || 0;
        const confirmed =
          typeof window !== 'undefined' && typeof window.confirm === 'function'
            ? window.confirm(
                `Esta sessão tem ${n} alteração(ões) não commitada(s) no worktree que serão PERDIDAS ao excluir. Continuar mesmo assim?`,
              )
            : false;
        if (!confirmed) {
          return { ok: false, error: 'cancelled', reason: 'dirty_worktree', changes: n };
        }
        try {
          await window.apolo.worktree.remove({ sessionId: id, force: true });
        } catch (err) {
          console.warn('[apolo] forced worktree removal failed:', err?.message || err);
        }
      }
      try {
        await apiFetch(`/api/code-ide/sessions/${id}/worktree`, { method: 'DELETE' });
      } catch {
        // best-effort — the soft-delete below clears the session anyway
      }
    }

    const res = await apiFetch(`/api/code/sessions/${id}`, { method: 'DELETE' });
    if (!res.ok) return { ok: false, error: res.error };
    set((state) => {
      const remainingPanes = (state.code.workspacePanes || []).filter((p) => p.sessionId !== id);
      const panePatch =
        remainingPanes.length === 0
          ? { workspacePanes: [], focusedPaneId: null, activeSessionId: null }
          : applyWorkspaceState(
              remainingPanes,
              state.code.focusedPaneId &&
                remainingPanes.some((p) => p.id === state.code.focusedPaneId)
                ? state.code.focusedPaneId
                : remainingPanes[0]?.id ?? null,
            );
      return {
        code: {
          ...state.code,
          sessions: state.code.sessions.filter((s) => s.id !== id),
          ...panePatch,
        },
      };
    });
    return { ok: true };
  },
};
});

// super-marathon E3+ — sessions are per-profile: when the active persona changes
// (useAuthStore.switchProfile dispatches this), refetch so RECENTS shows THAT profile's
// sessions (+ the shared legacy pool). GitHub/MCPs/skills/brain stay account-level, untouched.
if (typeof window !== 'undefined') {
  window.addEventListener('apolo:profile:changed', () => {
    const st = useAgentStore.getState();
    // Auditoria 2026-07-02 (chat-hardening S2 #5): a busca da sidebar persistia na
    // troca de perfil → as sessões do novo perfil ficavam filtradas pelo texto
    // antigo (podia parecer vazio). Zera junto com o refetch.
    if (st.code.sidebarSearch) st.setSidebarSearch('');
    st.refreshSessions();
  });
}
